import { index, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { pgTable } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';

// =============================================================================
// ENTITLEMENT GRANTS — G1. docs/CHANGE-CONTROL.md R184 ("G1 GO — timed promo
// grants as the comp mechanism", Jason's design): "comped" is never an
// unlimited backdoor flag — it is a TIMED TRIAL GRANT, "a trial setting that
// functions like comped with a timer (7 days, 2 weeks, 1 month), used
// dynamically with different promos."
//
// A grant IS trial-shaped on purpose: it resolves to EntitlementTier 'trial'
// (apps/api/lib/entitlements.ts's CAPABILITY_TABLE.trial === CAPABILITY_TABLE
// .paid, the existing trial===paid invariant) — never a fifth tier, never a
// bespoke capability list. The only new inputs are WHO (userId), WHY (label,
// free text — 'founder' | 'beta-tester' | 'launch-promo-14d' | anything a
// human names it) and WHEN (startsAt/endsAt) — clock-truth decides whether it
// is live, exactly like every other temporal fact in this codebase (the
// "don't trust the clock, don't trust the stored status string alone" law
// resolveEntitlement's own trialEnd/currentPeriodEnd checks already encode).
//
// Per-user via users.id (uuid) — this is a NEW table, so it takes the
// CURRENT-LAW shape (CLAUDE.md's corrected 2026-06-22 note: "identity =
// per-user users.id uuid, text-keyed user_id tables are migration debt") even
// though the sibling `subscriptions`/`billing_events` tables (billing.ts) are
// still text-keyed from launch week. No FK constraint is declared (Neon/
// Drizzle additive-only convention this codebase already follows for
// user-scoped tables — see subscriptions.ts, watchlist.ts, etc., none of
// which declare a literal REFERENCES either); the application layer is the
// single source of truth for the relationship, exactly as those tables
// already operate.
//
// Selective, per-person issuance is the point of the mechanism (Jason,
// scope-change note on this lane): there is no bulk-seed path here or in the
// SQL doc. A grant is written one INSERT at a time, by a human, naming a real
// user by their real uuid — see docs/design/g1-entitlement-grants-neon.sql.
//
// Resolution lives in entitlements.ts as a SIBLING pure function
// (resolveEntitlementWithGrants), not a reshape of resolveEntitlement's own
// signature — the RESOLUTION CALL SITE (requireEntitlement.ts's
// resolveRequestEntitlement / a future billing.routes.ts GET /entitlement)
// loads the newest non-revoked, non-expired grant row (if any) alongside the
// existing subscription row and lets the sibling function pick the BEST of
// the two. FROZEN-foundations law: resolveEntitlement itself is untouched.
//
// Additive only (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool,
// mirrored here, exported from index.ts. Nothing dropped or renamed.
// =============================================================================

/** No enum — a grant's label is a free-text promo/cohort name a human chooses
 *  at issuance time ('founder', 'beta-tester', 'launch-promo-14d', or any
 *  future promo string). Constraining this to a closed union would mean a
 *  schema change every time a new promo is run; the brief's own words are
 *  "used dynamically with different promos" — the label is DATA, not code. */
export const entitlementGrants = pgTable(
  'entitlement_grants',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    /** The real users.id uuid this grant belongs to. No FK declared (see
     *  module header) — application-enforced, matching every other
     *  user-scoped table's existing posture in this codebase. */
    userId: uuid('user_id').notNull(),
    /** Free-text promo/cohort label, e.g. 'founder', 'beta-tester',
     *  'launch-promo-14d'. Surfaced verbatim as `grantLabel` on GET
     *  /api/billing/entitlement so the FE can render an honest "why do I have
     *  this" note — never re-derived or paraphrased client-side. */
    label: varchar('label', { length: 64 }).notNull(),
    /** When this grant's window opens. Defaults to issuance time — a grant is
     *  live immediately unless a human deliberately back- or forward-dates it
     *  (e.g. "starts the day the promo page goes live," not before). */
    startsAt: timestamp('starts_at', { withTimezone: true }).notNull().default(sql`now()`),
    /** When this grant's window closes. Required — an entitlement_grants row
     *  is BY DEFINITION timed (the brief: "a trial setting that functions
     *  like comped with a timer"); there is no such thing as an un-timed
     *  grant in this table. An indefinite comp is a subscriptions row with
     *  plan='founder'/status='active', not a grant. */
    endsAt: timestamp('ends_at', { withTimezone: true }).notNull(),
    /** Set the instant a human revokes this grant early. NULL = never
     *  revoked. Nothing-unrecoverable: revoking never deletes the row — the
     *  grant's own history (who got what, when, why) stays queryable
     *  forever; a revoked-but-still-inside-its-window grant reads as
     *  EXPIRED for resolution purposes (see resolveEntitlementWithGrants),
     *  never silently un-revoked by the clock alone. */
    revokedAt: timestamp('revoked_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    userIdx: index('entitlement_grants_user_idx').on(t.userId),
    endsAtIdx: index('entitlement_grants_ends_at_idx').on(t.endsAt),
  }),
);

/** The minimal shape resolveEntitlementWithGrants needs from an
 *  `entitlement_grants` row — Pick-shaped like entitlements.ts's own
 *  SubscriptionRowLike, so a caller can pass a real DB row or a plain test
 *  fixture without importing Drizzle's row type. */
export interface EntitlementGrantRowLike {
  label: string;
  startsAt: Date | string;
  endsAt: Date | string;
  revokedAt: Date | string | null;
}
